import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { AppContext, useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import Home from "@/attendee/Home";
import MyTrip from "@/attendee/MyTrip";
import Schedule from "@/attendee/Schedule";
import Explore from "@/attendee/Explore";
import Challenge from "@/attendee/Challenge";
import More from "@/attendee/More";
import {
  ArrowLeft, Eye, Lock, Home as HomeIcon, CalendarDays, Compass, Trophy, Menu,
} from "lucide-react";

/**
 * Manager-only, READ-ONLY "Preview as Attendee" tool.
 *
 * The Manager stays authenticated as themselves — there is NO Clerk
 * impersonation, no attendee token, no attendee credentials. This screen reuses
 * the REAL attendee components (Home / My Trip / Schedule / Explore / Challenge /
 * More → Know / Updates / FAQs / Contacts) and mirrors the real attendee bottom
 * navigation and feature-toggle visibility for the SELECTED attendee_id, calling
 * the same attendee-safe endpoints. `enforce_attendee_access` authorizes managers
 * server-side and blocks ordinary attendees.
 *
 * ROUTING: the app runs inside a single Router. This component must NOT create
 * another <Router>. It uses local tab state and contains the reused components'
 * internal navigate() calls via a preview-aware `previewNavigate` supplied on
 * AppContext, so nothing escapes to the real Router except "Back to Manager".
 */

// Map an attendee-app path (used by reused components' internal navigation) to a
// preview tab so in-app links (e.g. Home → My Trip, profile chip → More) work.
function pathToTab(to) {
  if (typeof to !== "string") return null; // ignore navigate(-1) etc.
  const path = to.split("?")[0].replace(/\/+$/, "") || "/app";
  if (path === "/app") return "home";
  if (path === "/app/trip") return "trip";
  if (path === "/app/schedule") return "schedule";
  if (path === "/app/explore") return "explore";
  if (path.startsWith("/app/explore")) return "explore";
  if (path === "/app/challenge") return "challenge";
  if (path === "/app/more") return "more";
  return null; // unknown -> ignore (stay on current tab), never crash
}

function PreviewTabBar({ tabs, activeTab, onSelect }) {
  return (
    <nav
      data-testid="attendee-preview-tabbar"
      className="shrink-0 att-glass border-t border-[hsl(var(--pc-line))] text-cream"
    >
      <div className="grid px-1 pt-1.5 pb-2" style={{ gridTemplateColumns: `repeat(${tabs.length}, minmax(0, 1fr))` }}>
        {tabs.map((t) => {
          const Icon = t.icon;
          const active = activeTab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => onSelect(t.key)}
              data-testid={t.testid}
              className={`flex flex-col items-center gap-1 py-1.5 rounded-xl transition-colors ${
                active ? "text-[hsl(var(--pc-gold))]" : "text-[hsl(var(--pc-cream)/0.55)] hover:text-[hsl(var(--pc-cream)/0.85)]"
              }`}
            >
              <Icon className="h-5 w-5" strokeWidth={active ? 2.4 : 1.9} />
              <span className="text-[10px] font-semibold tracking-wide">{t.label}</span>
              <span className={`h-1 w-1 rounded-full ${active ? "bg-[hsl(var(--pc-gold))]" : "bg-transparent"}`} />
            </button>
          );
        })}
      </div>
    </nav>
  );
}

export default function AttendeePreview() {
  const { attendeeId } = useParams();
  const navigate = useNavigate(); // main app Router — used ONLY for "Back to Manager"
  const app = useApp();
  const eventId = app?.eventId || "presidents-club-2026";

  const [attendee, setAttendee] = useState(null);
  const [error, setError] = useState(false);
  const [activeTab, setActiveTab] = useState("home");
  const [gamVisible, setGamVisible] = useState(false);
  const [featureVis, setFeatureVis] = useState({});

  useEffect(() => {
    let ok = true;
    setAttendee(null);
    setError(false);
    setActiveTab("home");
    api.context(eventId, attendeeId)
      .then((d) => { if (ok) setAttendee(d.attendee); })
      .catch(() => { if (ok) setError(true); });
    api.gamVisible(eventId).then((r) => { if (ok) setGamVisible(!!r.visible); }).catch(() => {});
    api.getEvent(eventId).then((ev) => { if (ok) setFeatureVis(ev?.feature_visibility || {}); }).catch(() => {});
    return () => { ok = false; };
  }, [eventId, attendeeId]);

  const name = attendee?.full_name || attendee?.display_first_name || "attendee";
  const email = attendee?.email;

  // Same feature-toggle logic as the real AttendeeLayout bottom navigation.
  const on = (k) => featureVis[k] !== false; // default visible unless explicitly disabled
  const tabs = useMemo(() => {
    const t = [{ key: "home", label: "Home", icon: HomeIcon, testid: "attendee-preview-tab-home" }];
    if (on("schedule")) t.push({ key: "schedule", label: "Schedule", icon: CalendarDays, testid: "attendee-preview-tab-schedule" });
    if (on("explore")) t.push({ key: "explore", label: "Explore", icon: Compass, testid: "attendee-preview-tab-explore" });
    if (gamVisible && on("challenge")) t.push({ key: "challenge", label: "Challenge", icon: Trophy, testid: "attendee-preview-tab-challenge" });
    t.push({ key: "more", label: "More", icon: Menu, testid: "attendee-preview-tab-more" });
    return t;
  }, [featureVis, gamVisible]);

  // Preview-aware navigation: contains reused components' internal navigate()
  // calls to the preview's own tabs — nothing escapes to the real Router.
  const previewCtx = useMemo(() => ({
    ...app,
    attendeeId,
    currentPersona: attendee,
    previewMode: true,
    readOnly: true,
    demoMode: false, // show the attendee profile chip, not the demo switcher
    previewNavigate: (to) => {
      const tab = pathToTab(to);
      if (tab) setActiveTab(tab);
    },
  }), [app, attendeeId, attendee]);

  return (
    <div className="fixed inset-0 z-[200] flex flex-col bg-[hsl(var(--pc-ink))]" data-testid="attendee-preview-screen">
      {/* Persistent, unmistakable preview banner */}
      <div
        data-testid="attendee-preview-banner"
        className="flex items-center justify-between gap-3 bg-[hsl(var(--pc-gold))] px-4 py-2.5 text-[hsl(var(--pc-ink))]"
      >
        <div className="flex items-center gap-2 min-w-0">
          <Eye className="h-4 w-4 shrink-0" />
          <div className="min-w-0">
            <div data-testid="attendee-preview-title" className="text-sm font-bold leading-tight truncate">
              PREVIEW MODE — Viewing attendee experience for {name}
            </div>
            <div className="flex items-center gap-1 text-[11px] font-medium opacity-80 truncate">
              <Lock className="h-3 w-3" /> Read-only preview{email ? ` · ${email}` : ""}
            </div>
          </div>
        </div>
        <button
          data-testid="attendee-preview-back"
          onClick={() => navigate("/manager/attendees")}
          className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-[hsl(var(--pc-ink))] px-3 py-1.5 text-xs font-semibold text-[hsl(var(--pc-gold))] hover:opacity-90 transition-opacity"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Back to Manager
        </button>
      </div>

      {error ? (
        <div className="flex-1 grid place-items-center text-cream" data-testid="attendee-preview-error">
          <div className="att-card p-6 text-center max-w-sm">
            <div className="font-serif text-xl text-cream">Unable to load this attendee</div>
            <p className="mt-1.5 text-sm text-cream-dim">The attendee could not be resolved for preview.</p>
            <button
              onClick={() => navigate("/manager/attendees")}
              className="mt-4 inline-flex items-center gap-1.5 rounded-full att-glass px-4 py-2 text-sm font-semibold text-[hsl(var(--pc-gold))]"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Back to Manager
            </button>
          </div>
        </div>
      ) : (
        <div className="flex-1 overflow-hidden">
          <AppContext.Provider value={previewCtx}>
            <div className="h-full bg-[hsl(var(--pc-ink))] flex justify-center">
              <div className="att-bg relative w-full max-w-[30rem] h-full shadow-2xl overflow-hidden flex flex-col">
                {/* Read-only shield: block all write interactions inside the preview
                    viewport while keeping tab navigation (rendered below) usable. */}
                <main
                  className="flex-1 overflow-y-auto no-scrollbar text-cream"
                  data-testid="attendee-preview-viewport"
                  style={{ pointerEvents: "auto" }}
                >
                  {activeTab === "home" && <Home />}
                  {activeTab === "trip" && <MyTrip />}
                  {activeTab === "schedule" && <Schedule />}
                  {activeTab === "explore" && <Explore />}
                  {activeTab === "challenge" && <Challenge />}
                  {activeTab === "more" && <More />}
                </main>
                <PreviewTabBar tabs={tabs} activeTab={activeTab} onSelect={setActiveTab} />
              </div>
            </div>
          </AppContext.Provider>
        </div>
      )}
    </div>
  );
}
